import {
  AdjacentListsSupport,
  Alignment,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  Code,
  CodeBlock,
  CloudServices,
  Clipboard,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  HorizontalLine,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  Mention,
  Paragraph,
  PasteFromOffice,
  PictureEditing,
  RemoveFormat,
  SpecialCharacters,
  Strikethrough,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline
} from 'ckeditor5';
// Centralised, upgrade-safe toolbar theming (see file header). Imported here so
// every consumer of the shared config below inherits the styling.
import 'src/plugins/ckeditor/ckeditorTheme.css';

// Merge field options
export const mergeFieldsOptions = [
  { id: 'familyId', label: 'Family Name', aliases: ['Family ID'] },
  { id: 'upn', label: 'UPN', aliases: [] },
  { id: 'firstName', label: 'First Name', aliases: ['First name'] },
  { id: 'lastName', label: 'Last Name', aliases: ['Last name'] },
  { id: 'dob', label: 'DOB', aliases: ['Date of birth'] },
  { id: 'gender', label: 'Gender', aliases: ['Sex / Gender'] },
  { id: 'address', label: 'Address', aliases: [] },
  { id: 'email', label: 'Email', aliases: [] },
  {
    id: 'hospitalNumber',
    label: 'Hospital Number',
    aliases: ['Hospital number']
  },
  {
    id: 'healthcareId',
    label: 'Health ID Number',
    aliases: ['Healthcare ID']
  },
  { id: 'staffMember', label: 'Staff Member', aliases: ['Staff member'] },
  { id: 'title', label: 'Title', aliases: [] }
];

export const baseConfig = {
  licenseKey: 'GPL',
  // Menu bar retired in the toolbar redesign: it duplicated toolbar formatting,
  // never surfaced the custom insert/export actions, and made the editor feel
  // dated. The single toolbar below is now the sole set of controls.
  menuBar: {
    isVisible: false
  },
  heading: {
    options: [
      {
        model: 'paragraph',
        title: 'Paragraph',
        class: 'ck-heading_paragraph'
      },
      {
        model: 'heading1',
        view: 'h2',
        title: 'Heading 1',
        class: 'ck-heading_heading1'
      },
      {
        model: 'heading2',
        view: 'h3',
        title: 'Heading 2',
        class: 'ck-heading_heading2'
      },
      {
        model: 'heading3',
        view: 'h4',
        title: 'Heading 3',
        class: 'ck-heading_heading3'
      },
      {
        model: 'heading4',
        view: 'h5',
        title: 'Heading 4',
        class: 'ck-heading_heading4'
      },
      {
        model: 'heading5',
        view: 'h6',
        title: 'Heading 5',
        class: 'ck-heading_heading5'
      }
    ]
  },
  fontFamily: {
    supportAllValues: true
  },
  fontSize: {
    options: [10, 12, 14, 'default', 18, 20, 22],
    supportAllValues: true
  },
  image: {
    toolbar: [
      'toggleImageCaption',
      'imageTextAlternative',
      '|',
      'imageStyle:inline',
      'imageStyle:wrapText',
      'imageStyle:breakText',
      '|',
      'resizeImage'
    ]
  },
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: 'https://'
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true
    }
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
      'toggleTableCaption'
    ]
  }
};

export const basicPlugins = [
  AdjacentListsSupport,
  Alignment,
  Autoformat,
  AutoLink,
  BlockQuote,
  Bold,
  CloudServices,
  Clipboard,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  HorizontalLine,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  Mention,
  Paragraph,
  PasteFromOffice,
  PictureEditing,
  RemoveFormat,
  SpecialCharacters,
  Strikethrough,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline
];

/*
 * Toolbar layout — Concept 1 ("grouped, with a distinct smart-content cluster").
 *
 * Two explicit lines (the `'-'` item forces a line break — only honoured when
 * `shouldNotGroupWhenFull: true`):
 *   Line 1  Quiet standard formatting (undo/redo, paragraph style, B/I/U,
 *           links, media, alignment, lists, indent) — deliberately understated.
 *   Line 2  All app-specific actions on one row (they fit now the modal is
 *           wider): the field cluster (Template · Merge field, brand teal
 *           tgc-tb-brand), the logic actions (Condition · Repeat, purple
 *           tgc-tb-logic), the merge-field preview toggle, then the export
 *           group (PDF red · Word blue). Anchoring the merge-field picker on
 *           the left keeps its wide two-column panel inside the modal instead
 *           of forcing a horizontal scroll.
 *
 * Note on unregistered items: some consumers (read-only viewer, template
 * editors) don't register every custom plugin. CKEditor silently drops any
 * toolbar item whose component isn't registered, so the same array is safe to
 * share across all of them. The previous config also listed several phantom
 * items (insertMergeField, previewMergeFields, importWord, exportWord,
 * exportPdf, insertTemplate) that mapped to nothing and were dropped — removed.
 */
export const toolbar = {
  shouldNotGroupWhenFull: true,
  items: [
    // --- Line 1: standard formatting (quiet) ---
    'undo',
    'redo',
    '|',
    'heading',
    {
      label: 'Font styles',
      icon: 'text',
      items: ['fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor']
    },
    'removeFormat',
    '|',
    'bold',
    'italic',
    'underline',
    '|',
    'link',
    'insertImage',
    'insertTable',
    '|',
    'alignment',
    '|',
    'bulletedList',
    'numberedList',
    '|',
    'outdent',
    'indent',
    '-',
    // --- Line 2: all custom actions on one row (they fit now the modal is
    //     wider): field cluster (teal) · logic (purple) · preview · export ---
    'templateDropdown',
    'mergeFieldDropdown',
    'insertConditionalBlock',
    'insertRepeatingBlock',
    'mergeFieldPreviewDropdown',
    '|',
    'exportToPdf',
    'exportToDocs'
  ]
};
